import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import type { NextRouter } from 'next/router';
import React, { useEffect } from 'react';

import DesktopFilter from '@/components/menuV2/DesktopFilter/DesktopFilter';
import PageLinksDesktop from '@/components/menuV2/PageLinksDesktop/PageLinksDesktop';
import type {
  FilterRecordModelType,
  MenuHierarchyRecordModelType,
  MenuSortOptionRecordModelType,
} from '@/models/api';
import { DEFAULT_SORT } from '@/models/product/filters';
import { useStore } from '@/models/root-store';
import type { MenuPath } from '@/shared/types';
import { extractDynamicRouterKeys } from '@/utils/helpers';

import styles from './styles.module.css';

interface IFilterSortContainerDesktop {
  filterCmsData: FilterRecordModelType;
  sortCmsData: MenuSortOptionRecordModelType[];
  categoryMenuHierarchy?: MenuHierarchyRecordModelType;
  menuHierarchyCms?: MenuHierarchyRecordModelType;
  router: NextRouter;
  menuPath?: MenuPath;
  search?: boolean;
  isSearchPending?: boolean;
}

export const FilterSortContainerDesktop: React.FC<IFilterSortContainerDesktop> =
  observer(
    ({
      filterCmsData,
      categoryMenuHierarchy,
      menuHierarchyCms,
      router,
      menuPath,
      sortCmsData,
      search,
      isSearchPending,
    }) => {
      const filtersData = filterCmsData?.sections;
      const sorts = sortCmsData;
      const {
        productStore: {
          filters,
          productsCount,
          menuHierarchy,
          setMenuHierarchy,
        },
        generalStore,
      } = useStore();
      const {
        selectedFilters,
        filterSelected,
        isSortSelected,
        clearFilters,
        filterCount,
        setCurrentSelectedFilter,
        sort,
        isUrlApplied,
        setIsUrlApplied,
        routerQuery,
        setRouterQuery,
      } = filters;
      const { isFiltered, setFilter, applyFilters } = selectedFilters;
      const handleClearFilters = () => {
        clearFilters();
      };

      const defaultSort =
        menuHierarchyCms?.defaultSortOption?.value || 'featured';

      const extractFilterKeys = () => {
        // Extract dynamic route keys from pathname
        const dynamicKeys = extractDynamicRouterKeys(router);
        // Determine which keys belong to filters (excluding dynamic route keys)
        const filterKeys = _.without(
          Object.keys(selectedFilters),
          ...dynamicKeys,
        );

        return _.concat(filterKeys, 'sort');
      };

      // update the query params when filters or sort are changed
      const updateRouter = () => {
        const excludedKeys = extractFilterKeys();
        // Preserve initial query params, excluding filters & sort
        const initialQuery = _.omit(router.query, excludedKeys);

        // Construct updated query with selected filters & sort
        const filterQuery = _.mapValues(filterSelected, (values) =>
          _.isEmpty(values) ? undefined : _.join(values, ','),
        );

        const updatedQuery = {
          ...initialQuery,
          ...filterQuery,
          sort,
        };

        return updatedQuery;
      };

      // Apply filters from query params
      const compareFilters = () => {
        const parsedFilters = _.mapValues(router.query, (value: string) =>
          _.split(value, ','),
        );

        const includedKeys = extractFilterKeys();
        const filtersFromRouter = _.pick(parsedFilters, includedKeys);

        // compare the filters from query params with the selected filters
        const areFiltersEqual = _.isEqual(filtersFromRouter, {
          ...filterSelected,
          sort: [sort],
        });
        return { areFiltersEqual, filtersFromRouter };
      };

      const compareRouterQuery = () => {
        const parsedFilters = _.mapValues(router.query, (value: string) =>
          _.split(value, ','),
        );

        const includedKeys = extractFilterKeys();
        const filtersFromRouter = _.pick(parsedFilters, includedKeys);

        // compare routerQuery
        const areFiltersEqual = _.isEqual(routerQuery, router.query);
        return { areFiltersEqual, filtersFromRouter };
      };

      useEffect(() => {
        return () => {
          setRouterQuery(router.query);
        };
      }, [router.query]);

      useEffect(() => {
        if (!generalStore.ready || !isUrlApplied) return;
        const { areFiltersEqual } = compareFilters();
        if (areFiltersEqual) return;

        const query = updateRouter();
        const { pathname } = router;
        router.push({ pathname, query }, undefined, { shallow: true });
      }, [filterSelected, sort, generalStore.ready, isUrlApplied]);

      useEffect(() => {
        if (isSearchPending) return;
        if (!generalStore.ready || !isUrlApplied) return;
        const query = updateRouter();
        const { pathname } = router;
        router.replace({ pathname, query }, undefined, { shallow: true });
      }, [router.asPath, generalStore.ready, isUrlApplied]);

      useEffect(() => {
        if (!sorts?.length || !generalStore.ready) return;
        if (menuHierarchyCms && menuHierarchyCms.name === menuHierarchy?.name) {
          return;
        }
        setMenuHierarchy(menuHierarchyCms);
        // apply the filter and sort from query params for the initial load
        if (isUrlApplied) {
          filters.setSort(defaultSort || DEFAULT_SORT);
        }
      }, [
        generalStore.ready,
        defaultSort,
        menuHierarchyCms?.name,
        sortCmsData,
        filters,
      ]);

      useEffect(() => {
        if (!router.isReady || !generalStore.ready || isUrlApplied) return;
        const { areFiltersEqual, filtersFromRouter } = compareRouterQuery();
        if (areFiltersEqual) return;
        applyFilters(filtersFromRouter);
        if (!_.isEmpty(filtersFromRouter.sort)) {
          filters.setSort(_.first(_.get(filtersFromRouter as any, 'sort')));
        }
        setIsUrlApplied(true);
      }, [router.isReady, router.query, generalStore.ready, isUrlApplied]); // Runs on load & browser navigation

      return (
        <div
          className={`${styles.desktop_filter} sticky top-[102px] hidden h-full w-[300px] flex-col overflow-y-auto pb-[60px] pr-4 lg:flex`}
        >
          {categoryMenuHierarchy && (
            <PageLinksDesktop
              categoryMenuHierarchy={categoryMenuHierarchy}
              currentPath="menu"
              router={router}
              menuPath={menuPath}
            />
          )}
          {!search && <hr className="mb-5 mt-3" />}

          <div className="pl-2">
            <div className="">
              <button
                className="whitespace-nowrap pr-1 font-interMedium text-xs text-blueberry	"
                type="button"
                onClick={handleClearFilters}
              >
                Clear all
              </button>
            </div>
            <div className="mt-4 flex justify-between">
              <span className="font-interBold text-lg leading-5 md:text-2xl">
                Filter & sort
                <span className="ml-1 font-interMedium text-base text-grey-dark">
                  ({filterCount})
                </span>
              </span>
            </div>
            {productsCount > 0 && !search ? (
              <div className="py-3 font-interMedium text-grey-dark">
                {productsCount} items
              </div>
            ) : (
              <div className={`${search ? '' : 'h-14 pb-2'}`}>&nbsp;</div>
            )}
            <DesktopFilter
              search={search}
              sorts={sorts}
              filtersData={filtersData}
              isSortSelected={isSortSelected}
              setCurrentSelectedFilter={setCurrentSelectedFilter}
              filters={filters}
              setFilter={setFilter}
              isFiltered={isFiltered}
            />
          </div>
        </div>
      );
    },
  );
